import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import {
  Field,
  Input,
  RadioGroup,
  SettingsEditDialog,
  toasterApi,
} from '@terra/ui-design-system';
import type { ClientTlsCertificateEntry } from '../../../store/proxy-store';
import { CertificateFileField } from './CertificateFileField';
import {
  buildManualCertificateEntry,
  CERTIFICATE_ACCEPT_BY_TARGET,
  EMPTY_MANUAL_FORM,
  MAX_CERTIFICATE_FILE_BYTES,
  readFileAsBase64,
  setCertificateFile,
  switchCertificateFormat,
  toCertificateFormat,
  validateManualForm,
  type CertificateFileTarget,
  type ManualFormState,
} from './client-tls-form.model';

type ClientTlsManualEntryDialogProps = {
  open: boolean;
  onClose: () => void;
  onAdd: (entry: ClientTlsCertificateEntry) => void;
};

export function ClientTlsManualEntryDialog({
  open,
  onClose,
  onAdd,
}: ClientTlsManualEntryDialogProps) {
  const t = useTranslations('settings.clientTls');
  const [form, setForm] = useState<ManualFormState>(EMPTY_MANUAL_FORM);
  const { trimmedOrigin, hasOriginScheme, isValid } = validateManualForm(form);
  const formatOptions = useMemo(
    () => [
      {
        value: 'pem',
        label: t('dialog.pemLabel'),
        description: t('dialog.pemDescription'),
      },
      {
        value: 'pfx',
        label: t('dialog.pfxLabel'),
        description: t('dialog.pfxDescription'),
      },
    ],
    [t],
  );
  const selectedFormatOption = formatOptions.find(
    (option) => option.value === form.format,
  );

  const close = () => {
    setForm(EMPTY_MANUAL_FORM);
    onClose();
  };

  const handleFiles = async ({
    target,
    files,
  }: {
    target: CertificateFileTarget;
    files: File[];
  }) => {
    const file = files[0];

    if (!file) return;
    if (file.size > MAX_CERTIFICATE_FILE_BYTES) {
      toasterApi.error({
        title: t('toasts.fileTooLargeTitle'),
        description: t('toasts.fileTooLargeDescription'),
      });

      return;
    }

    try {
      const content =
        target === 'pfx' ? await readFileAsBase64(file) : await file.text();

      setForm((previous) =>
        setCertificateFile({
          form: previous,
          target,
          content,
          fileName: file.name,
        }),
      );
    } catch (error) {
      toasterApi.error({
        title: t('toasts.readFailedTitle'),
        description:
          error instanceof Error
            ? error.message
            : t('toasts.readFailedDescriptionUnknown'),
      });
    }
  };

  const clearFile = (target: CertificateFileTarget) =>
    setForm((previous) =>
      setCertificateFile({
        form: previous,
        target,
        content: undefined,
        fileName: undefined,
      }),
    );

  const handleSubmit = () => {
    if (!isValid) {
      const missingScheme = Boolean(trimmedOrigin) && !hasOriginScheme;

      toasterApi.error({
        title: missingScheme
          ? t('toasts.invalidOriginTitle')
          : t('toasts.missingFieldsTitle'),
        description: missingScheme
          ? t('toasts.invalidOriginDescription')
          : form.format === 'pem'
            ? t('toasts.missingFieldsDescriptionPem')
            : t('toasts.missingFieldsDescriptionPfx'),
      });

      return;
    }

    onAdd(buildManualCertificateEntry({ form, id: crypto.randomUUID() }));
    toasterApi.success({
      title: t('toasts.addedTitle'),
      description: t('toasts.addedDescription', {
        name: form.name.trim(),
        origin: trimmedOrigin,
      }),
    });
    close();
  };

  const renderFileField = ({
    target,
    label,
    fileName,
  }: {
    target: CertificateFileTarget;
    label: string;
    fileName?: string;
  }) => (
    <CertificateFileField
      label={label}
      accept={CERTIFICATE_ACCEPT_BY_TARGET[target]}
      fileName={fileName}
      onFiles={(files) => handleFiles({ target, files })}
      onClear={() => clearFile(target)}
    />
  );

  return (
    <SettingsEditDialog
      open={open}
      title={t('dialog.title')}
      description={t('dialog.description')}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
      onSubmit={handleSubmit}
      submitText={t('dialog.add')}
      cancelText={t('dialog.cancel')}
      isSubmitDisabled={!isValid}
    >
      <VStack gap={3} alignItems="stretch" w="full">
        <Field.Root>
          <Field.Label fontSize="sm">{t('dialog.format')}</Field.Label>
          <RadioGroup.Root
            size="sm"
            value={form.format}
            onValueChange={(details) =>
              setForm((previous) =>
                switchCertificateFormat({
                  form: previous,
                  format: toCertificateFormat(details.value),
                }),
              )
            }
          >
            <HStack gap="4">
              {formatOptions.map((option) => (
                <RadioGroup.Item key={option.value} value={option.value}>
                  <RadioGroup.ItemControl />
                  <RadioGroup.ItemText fontSize="sm">
                    {option.label}
                  </RadioGroup.ItemText>
                  <RadioGroup.ItemHiddenInput />
                </RadioGroup.Item>
              ))}
            </HStack>
          </RadioGroup.Root>
          {selectedFormatOption ? (
            <Field.HelperText fontSize="xs">
              {selectedFormatOption.description}
            </Field.HelperText>
          ) : null}
        </Field.Root>

        <Field.Root>
          <Field.Label fontSize="sm">{t('dialog.name')}</Field.Label>
          <Input
            size="sm"
            placeholder={t('dialog.namePlaceholder')}
            value={form.name}
            onChange={(event) =>
              setForm((previous) => ({ ...previous, name: event.target.value }))
            }
          />
        </Field.Root>
        <Field.Root invalid={Boolean(trimmedOrigin) && !hasOriginScheme}>
          <Field.Label fontSize="sm">{t('dialog.origin')}</Field.Label>
          <Input
            size="sm"
            placeholder={t('dialog.originPlaceholder')}
            value={form.origin}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                origin: event.target.value,
              }))
            }
          />
          <Field.HelperText fontSize="xs">
            {t('dialog.originHelper')}
          </Field.HelperText>
        </Field.Root>

        {form.format === 'pem' ? (
          <>
            {renderFileField({
              target: 'cert',
              label: t('dialog.certificateLabel'),
              fileName: form.certFileName,
            })}
            {renderFileField({
              target: 'key',
              label: t('dialog.privateKeyLabel'),
              fileName: form.keyFileName,
            })}
          </>
        ) : (
          renderFileField({
            target: 'pfx',
            label: t('dialog.pfxBundleLabel'),
            fileName: form.pfxFileName,
          })
        )}

        <Field.Root>
          <Field.Label fontSize="sm">{t('dialog.passphraseLabel')}</Field.Label>
          <Input
            size="sm"
            type="password"
            placeholder={t('dialog.passphrasePlaceholder')}
            value={form.passphrase}
            onChange={(event) =>
              setForm((previous) => ({
                ...previous,
                passphrase: event.target.value,
              }))
            }
          />
        </Field.Root>
      </VStack>
    </SettingsEditDialog>
  );
}
